import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import { useLoaderData, useParams } from 'react-router-dom';
import { API } from '../../api';
import AppConstants from '../../helpers/AppConstants';
import { Utils } from '../../helpers/Utils';
import { EditCandidacyModalButton } from './EditCandidacyModalButton';
import QueryString from 'qs';

export const JobClosedCandidacies = ({ showAdminActions = true }) => {
    const loaderData = useLoaderData();
    const { companySlug } = useParams();
    const job = Utils.deepFlatten(loaderData.job);

    const candidacyStatusEnums = loaderData.candidacyStatusEnums;
    const company = job.company;

    const candidaciesWithContactData = job.candidacies
        .filter((candidacy) => candidacy.contact != null)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const stepOptions = [25, 50, 100];
    const [candidacies, setCandidacies] = useState(candidaciesWithContactData);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);

    useEffect(() => {
        if (!page) return;

        const pagedCandidacies = candidaciesWithContactData.slice(
            page * pageSize - pageSize,
            page * pageSize,
        );

        setPage(page);
        setCandidacies(pagedCandidacies);
        setTotalItems(candidaciesWithContactData.length);
    }, [page, pageSize]);

    const getCandidacyUrl = (jobId, candidacyId) =>
        companySlug
            ? `/${companySlug}/jobs/${jobId}/candidacies/${candidacyId}`
            : `/jobs/${jobId}/candidacies/${candidacyId}`;

    const candidacyDataColumns = [
        {
            header: 'Name',
            property: 'contact.lastName',
            render: (candidacy) => {
                return (
                    <Anchor
                        href={getCandidacyUrl(job.id, candidacy.id)}
                        label={`${candidacy.contact.firstName} ${candidacy.contact.lastName}`}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Status',
            property: 'status',
            sortable: true,
        },
        {
            header: 'Last Updated',
            property: 'updatedAt',
            render: (candidacy) => {
                return new Date(candidacy.updatedAt).toLocaleDateString(
                    'en-us',
                    {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    },
                );
            },
            sortable: true,
        },
        ...(showAdminActions
            ? [
                {
                    header: 'Action',
                    property: 'id',
                    sortable: false,
                    primary: true,
                    render: (candidacy) => {
                        return (
                            <EditCandidacyModalButton
                                candidacy={candidacy}
                                candidacyStatusEnums={candidacyStatusEnums}
                            />
                        );
                    },
                },
            ]
            : []),
    ];

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize != newPageSize) {
            setPageSize(newPageSize);
        }
        setPage(page);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Heading level="2">
                        {job.title} ({company.name})
                    </Heading>
                </Box>

                <Box>
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="3">Closed Candidacies</Heading>
                    </Box>

                    <Box elevation="large" pad="medium" fill="horizontal">
                        <DataTable
                            primaryKey="id"
                            sortable
                            data={candidacies}
                            columns={candidacyDataColumns}
                        />
                    </Box>
                </Box>

                <Box align="center" justify="center" pad="small">
                    <Pagination
                        numberItems={totalItems}
                        onChange={({ page, startIndex, endIndex }) => {
                            handlePageChange({
                                page,
                                startIndex,
                                endIndex,
                            });
                        }}
                        step={pageSize}
                        stepOptions={stepOptions}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

const getCandidacyStatusEnums = async () => {
    const query = `query myQuery {
        getFilterFields {
            candidacyStatuses {
                id
                name
            }
        }
    }`;

    try {
        const graphqlResponse = await API.post('graphql', { query });
        return (
            graphqlResponse.data.data?.getFilterFields?.candidacyStatuses || []
        );
    } catch (error) {
        console.log(error);
        return [];
    }
};

const getJob = async (jobId) => {
    const query = QueryString.stringify({
        populate: {
            candidacies: {
                populate: ['contact'],
                filters: {
                    status: {
                        $in: AppConstants.closedCandidacyStatuses,
                    },
                },
            },
            company: true,
        },
    });

    try {
        const response = await API.get(`/api/jobs/${jobId}?${query}`);
        return response.data.data;
    } catch (error) {
        console.log(error);
        return {};
    }
};

export async function loader({ params }) {
    const job = await getJob(params.jobId);
    const candidacyStatusEnums = await getCandidacyStatusEnums();
    return { job, candidacyStatusEnums };
}
